import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { slk } from 'survey-core';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';

import { loadCommunicationTemplates } from '../../store/communicationService';
import { loadCommunicationTypes, loadFormTypes } from '../../store/directusService';
import { createForm, loadForms, updateForm } from '../../store/participantService';
import {
    COMMUNICATION_TEMPLATES_ERROR,
    COMMUNICATION_TYPES_ERROR
} from '../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import CheckBox from '../formElements/CheckBox';
import SingleSelect from '../formElements/SingleSelect';
import LoadingSpinner from '../ui/LoadingSpinner';
import ScreenError from '../ui/messageBanners/ScreenError';

import { themeJson } from './theme';
import { validate } from './validateForm';

import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';
import form from '../../commonStyles/formStyles.module.css';
import local from './formStyles/formStyle.module.css';
import classes from './formStyles/formToolbar.module.css';

const VITE_APP_SURVEYJS_BUILDER_LICENCE = import.meta.env.VITE_APP_SURVEYJS_BUILDER_LICENCE;
const acceptedRoles = [SUPERUSER];

const FormBuilder = () => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    slk(VITE_APP_SURVEYJS_BUILDER_LICENCE); // use slk here

    const questionTypes = [
        'text',
        'comment',
        'checkbox',
        'radiogroup',
        'dropdown',
        'boolean',
        'rating',
        'matrix',
        'matrixdropdown',
        'matrixdynamic',
        'multipletext',
        'panel',
        'paneldynamic',
        'expression',
        'ranking',
        'buttongroup',
        'flowpanel'
    ];

    const [creator, setCreator] = useState(
        new SurveyCreator({
            showDesignerTab: true,
            showQuestionNumbers: true,
            showLogicTab: true,
            showThemeTab: false,
            showJSONEditorTab: true,
            isAutoSave: false,
            questionTypes: questionTypes
        })
    );
    const initialState = {
        id: '',
        name: '', //creator.survey.title,
        code: '',
        date_created: '',
        data: '', //JSON.parse(creator.text),
        inactive: false,
        typeId: '',
        emailTemplateId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [formChanged, setFormChanged] = useState(false);
    const [arrayEmailTemplates, setArrayEmailTemplates] = useState([]);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentForm = useSelector((state) => state.entities.participantService.currentForm);
    const formsParticipant = useSelector(
        (state) => state.entities.participantService.formsParticipant
    );
    const communicationTemplates = useSelector(
        (state) => state.entities.communicationService.communicationTemplates
    );
    const communicationTypes = useSelector(
        (state) => state.entities.directusService.communicationTypes
    );
    const formTypes = useSelector((state) => state.entities.directusService.formTypes);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadCommunicationTemplates());
        dispatch(loadCommunicationTypes());
        dispatch(loadFormTypes());
    }, []);

    useEffect(() => {
        if (Object.keys(currentForm).length > 0) setNewEntry(currentForm);
    }, [currentForm]);

    useEffect(() => {
        if (formChanged) {
            dispatch(loadForms());
            navigate('/manage_form');
        }
    }, [formChanged]);

    useEffect(() => {
        const hasResults =
            Object.keys(newEntry.data).length > 0 && Object.keys(formsParticipant).length > 0;
        const surveyOptions = {
            showDesignerTab: !hasResults,
            showLogicTab: !hasResults,
            showThemeTab: false,
            showJSONEditorTab: true,
            isAutoSave: false,
            questionTypes: questionTypes
        };

        setCreator(new SurveyCreator(surveyOptions));
    }, [formsParticipant, newEntry]);

    useEffect(() => {
        creator.theme = themeJson;
        if (Object.keys(newEntry.data).length > 0) {
            creator.text = JSON.stringify(newEntry.data);
        }
        if (Object.keys(formsParticipant).length < 1) {
            creator.saveSurveyFunc = () => {
                onSubmitForm();
            };
        }
    }, [creator, newEntry]);

    useEffect(() => {
        if (communicationTemplates?.length < 1 || communicationTypes?.length < 1) return;
        const emailTypeId =
            communicationTypes.find((el) => el.name.toLowerCase() === 'email')?.id || '';
        const emailTemplates = communicationTemplates.filter((el) => el.typeId === emailTypeId);
        setArrayEmailTemplates(emailTemplates);
    }, [communicationTemplates, communicationTypes]);

    useEffect(() => {
        if (!newEntry.name || !newEntry.data) return;
        if (
            newEntry.inactive !== currentForm?.inactive ||
            (newEntry.typeId && newEntry.typeId !== currentForm?.typeId) ||
            (newEntry.emailTemplateId && newEntry.emailTemplateId !== currentForm?.emailTemplateId)
        ) {
            // eslint-disable-next-line
            const { type, ...rest } = newEntry;
            dispatch(currentForm?.id ? updateForm(rest) : createForm(rest));
        }
    }, [newEntry.inactive, newEntry.typeId, newEntry.emailTemplateId]);

    // HELPER FNS
    const loadingError = () => {
        if (communicationTemplates?.length < 1) return COMMUNICATION_TEMPLATES_ERROR;
        if (communicationTypes?.length < 1) return COMMUNICATION_TYPES_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmitForm = () => {
        const newErrors = validate(creator.survey);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        // eslint-disable-next-line
        const { type, ...rest } = newEntry;
        const payload = {
            ...rest,
            name: creator.survey.title,
            data: JSON.parse(creator.text)
        };
        newEntry.id ? dispatch(updateForm(payload)) : dispatch(createForm(payload));
        setFormChanged(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={local.wrapper}>
            {Object.values(errors).some((el) => el.error) && (
                <div className={local.formBuilderErrorWrapper}>
                    {Object.entries(errors).map((el) =>
                        el[1].error ? (
                            <div key={el[0]}>
                                <ScreenError
                                    error={el[1]}
                                    name={el[0]}
                                    clearError={clearError}
                                    customClass="relativePos"
                                />
                            </div>
                        ) : null
                    )}
                </div>
            )}

            <div className={classes.formBuilder}>
                <SurveyCreatorComponent creator={creator} />
            </div>
            <form className={form.formWrapper} data-testid="form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <CheckBox
                            id="inactive"
                            label="Active"
                            disabled={!hasRole(acceptedRoles, roles)}
                            checked={!newEntry.inactive}
                            customClass="inline"
                            onChange={(e) => onUpdate('inactive', !e.target.checked)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'typeId'}
                            label={'Form Type'}
                            placeholder="Select form type..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={formTypes || []}
                            selectedId={currentForm?.typeId || ''}
                            selected={formTypes?.find((el) => el.id === newEntry?.typeId) || {}}
                            onChange={(chosenId) => onUpdate('typeId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'emailTemplateId'}
                            label={'Email Template'}
                            placeholder="Select email template..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={arrayEmailTemplates || []}
                            selectedId={currentForm?.emailTemplateId || ''}
                            selected={
                                arrayEmailTemplates?.find(
                                    (el) => el.id === newEntry?.emailTemplateId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdate('emailTemplateId', chosenId)}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default FormBuilder;
